import type { ReactNode } from 'react';
import { useT } from '@/shared/i18n';
import { useTheme } from '@/app/providers/useTheme';
import { Icon } from '@/shared/ui/icon';
import { cn } from '@/shared/lib/cn';

/**
 * Public-shell theme toggle — icon-only Sun/Moon button.
 *
 * The visible glyph indicates the *target* theme (the result of the
 * click), not the current state — clicking a sun means "go light," a moon
 * means "go dark." The accessible name follows the same direction
 * ("Switch to light mode" / "Switch to dark mode") so AT users get the
 * same intent as sighted users.
 *
 * Compact 36 px square sized to balance the locale toggle's height. Quiet
 * 1 px subtle border + raised-surface hover so the control reads as
 * intentional without becoming a heavy chip. Token-only colors (no raw
 * hex) — every visible color routes through a CSS variable so both themes
 * inherit naturally.
 */
const controlClasses =
  'inline-flex h-9 w-9 items-center justify-center rounded-md ' +
  'bg-transparent ' +
  'text-[var(--text-primary)] ' +
  'transition-colors duration-base ease-standard ' +
  'hover:bg-[var(--surface-raised)] ' +
  'focus-visible:outline-none';

export function PublicThemeToggle(): ReactNode {
  const { t } = useT();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const label = isDark
    ? t('theme.toggle.to-light.label')
    : t('theme.toggle.to-dark.label');
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={label}
      aria-pressed={isDark}
      data-testid="theme-toggle"
      className={cn(controlClasses)}
    >
      {isDark ? (
        <Icon name="sun" size={18} />
      ) : (
        <Icon name="moon" size={18} />
      )}
    </button>
  );
}

export default PublicThemeToggle;
